import React, { useState, useEffect } from 'react';
import { useReferral } from '../context/ReferralContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, doc, getDoc, limit, orderBy, updateDoc, increment, setDoc } from 'firebase/firestore';

const ReferralSection = () => {
  const { currentUser } = useAuth();
  const { 
    referralCode, 
    referrals, 
    referralStats, 
    loading,
    fetchReferrals,
    getReferralLink 
  } = useReferral();
  
  const [copied, setCopied] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const referralLink = getReferralLink();
  
  // Force refresh referrals data when component mounts and periodically
  useEffect(() => {
    console.log('ReferralSection mounted, fetching referrals...');
    fetchReferrals();
    
    // Set up a periodic refresh for referrals (every 30 seconds)
    const intervalId = setInterval(() => {
      console.log('Auto-refreshing referrals...');
      fetchReferrals();
    }, 30000);
    
    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, [fetchReferrals]);
  
  // New effect to auto-fix wallet balances on load
  useEffect(() => {
    if (!currentUser) return;
    
    const autoFixReferralRewards = async () => {
      console.log('Auto-checking for referral reward issues...');
      try {
        // First get user data
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) return;
        
        // Get all users who used this referral code
        const allUsersRef = collection(db, 'users');
        const q = query(allUsersRef, where('referrerUID', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        const referralCount = querySnapshot.size;
        
        if (referralCount === 0) return; // No referrals to check
        
        // Get the wallet balance
        const walletRef = doc(db, 'users', currentUser.uid, 'wallets', 'balances');
        const walletDoc = await getDoc(walletRef);
        const currentBalance = walletDoc.exists() ? (walletDoc.data().walletA || 0) : 0;
        
        const REFERRAL_REWARD = 1000;
        const expectedBalance = referralCount * REFERRAL_REWARD;
        
        // If balance is less than expected, fix it
        if (currentBalance < expectedBalance) {
          console.log(`Auto-fixing: Found ${referralCount} referrals but only ${currentBalance} coins`);
          const amountToAdd = expectedBalance - currentBalance;
          
          // Update the wallet balance
          if (walletDoc.exists()) {
            await updateDoc(walletRef, {
              walletA: increment(amountToAdd)
            });
          } else {
            await setDoc(walletRef, {
              walletA: expectedBalance,
              walletB: 0
            });
          }
          
          // Also update sources document
          const sourcesRef = doc(db, 'users', currentUser.uid, 'wallets', 'sources');
          const sourcesDoc = await getDoc(sourcesRef);
          
          if (sourcesDoc.exists()) {
            await updateDoc(sourcesRef, {
              referrals: increment(amountToAdd)
            });
          } else {
            await setDoc(sourcesRef, {
              referrals: amountToAdd,
              ads: 0,
              login: 0,
              accuracyBonus: 0
            });
          }
          
          // Update total earnings in user document
          await updateDoc(userRef, {
            totalReferralEarnings: expectedBalance
          });
          
          console.log(`Auto-fixed: Added ${amountToAdd} coins to wallet`);
          
          // Refresh to show the updated values
          setTimeout(() => fetchReferrals(), 1000);
        }
      } catch (error) {
        console.error('Error auto-fixing referral rewards:', error);
      }
    };
    
    // Run the auto-fix
    autoFixReferralRewards();
  }, [currentUser, fetchReferrals]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReferrals();
    setTimeout(() => setRefreshing(false), 1000);
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Kept fixWalletBalance for auto-recovery, but it won't be directly exposed in the UI
  const fixWalletBalance = async () => {
    if (!currentUser) return;
    
    setRefreshing(true);
    try {
      // Get all users who used this referral code
      const allUsersRef = collection(db, 'users');
      const q = query(allUsersRef, where('referrerUID', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const referralCount = querySnapshot.size;
      
      if (referralCount === 0) {
        console.log('No referrals found');
        return { success: false, error: 'No referrals found' };
      }
      
      // Calculate expected balance
      const REFERRAL_REWARD = 1000;
      const expectedBalance = referralCount * REFERRAL_REWARD;
      
      // Get current wallet balance
      const walletRef = doc(db, 'users', currentUser.uid, 'wallets', 'balances');
      const walletDoc = await getDoc(walletRef);
      const currentBalance = walletDoc.exists() ? (walletDoc.data().walletA || 0) : 0;
      
      // Get current sources
      const sourcesRef = doc(db, 'users', currentUser.uid, 'wallets', 'sources');
      const sourcesDoc = await getDoc(sourcesRef);
      const currentReferralSource = sourcesDoc.exists() ? (sourcesDoc.data().referrals || 0) : 0;
      
      // If wallet balance is less than expected, update it
      if (currentBalance < expectedBalance) {
        const amountToAdd = expectedBalance - currentBalance;
        
        console.log(`Fixing wallet balance: Adding ${amountToAdd} coins`);
        
        // Update wallet balance
        if (walletDoc.exists()) {
          await updateDoc(walletRef, {
            walletA: increment(amountToAdd)
          });
        } else {
          await setDoc(walletRef, {
            walletA: expectedBalance,
            walletB: 0
          });
        }
        
        // Update sources document
        if (sourcesDoc.exists()) {
          await updateDoc(sourcesRef, {
            referrals: expectedBalance
          });
        } else {
          await setDoc(sourcesRef, {
            referrals: expectedBalance,
            ads: 0,
            login: 0,
            accuracyBonus: 0
          });
        }
        
        // Also update the user document total
        await updateDoc(doc(db, 'users', currentUser.uid), {
          totalReferralEarnings: increment(amountToAdd)
        });
        
        return { success: true, added: amountToAdd };
      } else {
        console.log('Wallet balance is correct or higher than expected');
        return { success: true, added: 0 };
      }
    } catch (error) {
      console.error('Error fixing wallet balance:', error);
      return { success: false, error: error.message };
    } finally {
      setRefreshing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }
  
  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4">Referral Program</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column - Referral Code & Stats */}
        <div>
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Your Referral Code</p>
            <div className="flex items-center">
              <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-l-md font-mono text-lg flex-grow">
                {referralCode}
              </div>
              <button 
                onClick={() => copyToClipboard(referralCode)}
                className="bg-blue-600 text-white px-3 py-2 rounded-r-md hover:bg-blue-700 transition"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Share Your Referral Link</p>
            <div className="flex items-center">
              <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-l-md text-sm truncate flex-grow">
                {referralLink}
              </div>
              <button 
                onClick={() => copyToClipboard(referralLink)}
                className="bg-blue-600 text-white px-3 py-2 rounded-r-md hover:bg-blue-700 transition"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Referrals</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{referralStats.totalReferrals}</p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Earnings</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{referralStats.totalEarnings} coins</p>
            </div>
          </div>
        </div>
        
        {/* Right Column - Referral Rewards Description */}
        <div>
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Referral Rewards</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Share your referral code with friends and earn rewards!
            </p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>For each successful referral</span>
                <span className="font-semibold text-green-600 dark:text-green-400">1000 coins</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Bonus for every 5 referrals</span>
                <span className="font-semibold text-green-600 dark:text-green-400">+1000 coins</span>
              </div>
              <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                <p>How it works:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Share your referral code or link with friends</li>
                  <li>When they sign up using your code, they become your referral</li>
                  <li>You earn 1000 coins for each successful referral</li>
                  <li>Get a bonus of 1000 coins for every 5 referrals you make</li>
                  <li>There's no limit to how many friends you can refer!</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Referrals List */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <button
            onClick={() => setShowReferrals(!showReferrals)}
            className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            <span className="font-medium">Your Referrals ({referralStats.totalReferrals})</span>
            <svg 
              className={`w-5 h-5 ml-2 transform transition-transform ${showReferrals ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          <button 
            onClick={handleRefresh}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            disabled={refreshing}
          >
            <svg 
              className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        
        {showReferrals && (
          <div className="mt-4 overflow-x-auto">
            {referrals.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Username</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reward</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {referrals.map((referral) => (
                    <tr key={referral.uid}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{referral.displayName}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{referral.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {referral.username || 'Not set'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {referral.joinDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400">
                        1000 coins
                        {/* Show milestone bonus badge for every 5th referral */}
                        {(referrals.indexOf(referral) + 1) % 5 === 0 && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                            +1000 bonus
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  You haven't referred anyone yet. Share your referral code to start earning coins!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ReferralSection; 